import { Select, SegmentedControl, Group, Stack, Text } from "@mantine/core"
import {
  MonthPickerInput,
  YearPickerInput,
  DatePickerInput,
} from "@mantine/dates"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { match } from "ts-pattern"
import { DefaultButton, PrimaryButton } from "../../../../-components/Buttons"
import { SelectEntity } from "../../../../-components/entity-select"
import { Page } from "../../../../-components/Page"
import { PlanScheduleDto } from "../../../../@client/Theasoft_ApiServer_SchedulePlans"
import { SimpleSeason } from "../../../../@client/Theasoft_ApiServer_Seasons"
import { WeekPicker } from "../../../../blocks/dates/week-picker"
import { dayJstoDateOnly } from "../../../../utils/dayjs-to-date-only"
import { showErrorNotification } from "../../../../utils/form-submission-notifications"
import { PublishedPlanList } from "./PublishedPlanList"
import { getDateOnly } from "../../../../-components/date-only"
import { PublishPlanLayoutControls } from "./PusblishPlanLayoutControls"
import { Suspense } from "react"
import { FormButtonGroup } from "../../../../-components/form-button-group"
import { SuspenseWithErrorBoundary } from "../../../../-components/suspense"
import { useSuspenseQueryGet } from "../../../../typed-client/hooks/use-suspense-query-get"
export function SchedulesPage() {
  const { t } = useTranslation("app_server")
  const [dateRange, setDateRange] = React.useState<[Date | null, Date | null]>([
    null,
    null,
  ])
  const [productionId, setProductionId] = React.useState<null | number>(null)
  const { data: plans } = useSuspenseQueryGet(
    "/api/Plans/GetAvailablePlans",
    null
  )
  const [plan, setPlan] = React.useState<null | PlanScheduleDto>(
    plans[0] || null
  )
  const [season, setSeason] = React.useState<null | SimpleSeason>(null)
  const [selectedWeek, setSelectedWeek] = React.useState<null | Date>(null)
  const [selectedYear, setSelectedYear] = React.useState<null | Date>(null)
  const [selectedMonth, setSelectedMonth] = React.useState<null | Date>(null)
  const [format, setFormat] = React.useState<"PDF" | "Word">("PDF")
  const createLink = ({ download }: { download: boolean }) =>
    `/api/Plans/export?planId=${plan!.id}&start=${dayjs(dateRange[0]).format(
      "YYYY-MM-DD"
    )}&end=${dayjs(dateRange[1]).format("YYYY-MM-DD")}${
      productionId !== null ? `&productionId=${productionId}` : ""
    }&format=${format}&download=${download ? "true" : "false"}`
  const clearInputs = () => {
    setSeason(null)
    setSelectedWeek(null)
    setSelectedYear(null)
    setSelectedMonth(null)
    setProductionId(null)
  }
  const valid =
    plan &&
    dateRange[0] &&
    dateRange[1] &&
    (plan?.isProductionRequired ? productionId : true)

  const updateDateRange = (v: [Date | null, Date | null]) => {
    const planStart = plan?.start ? dayjs(plan.start) : null
    const planEnd = plan?.end ? dayjs(plan.end) : null

    const nextStart =
      v[0] === null
        ? null
        : planStart && dayjs(v[0]).isBefore(planStart)
          ? planStart.toDate()
          : new Date(v[0])
    const nextEnd =
      v[1] === null
        ? null
        : planEnd && dayjs(v[1]).isAfter(planEnd)
          ? planEnd.toDate()
          : new Date(v[1])

    setDateRange((prev) => {
      const prevStart = prev[0]?.getTime() ?? null
      const prevEnd = prev[1]?.getTime() ?? null
      const start = nextStart?.getTime() ?? null
      const end = nextEnd?.getTime() ?? null

      if (prevStart === start && prevEnd === end) {
        return prev
      }

      return [nextStart, nextEnd]
    })
  }

  React.useEffect(() => {
    const today = dayjs()
    match(plan?.schedulePeriodType)
      .with("Seasons", () => {
        const currentSeason =
          plan?.seasons.find(
            (s) =>
              dayjs(today).isSameOrAfter(s.startDate) &&
              dayjs(today).isSameOrBefore(s.endDate)
          ) ||
          plan?.seasons[0] ||
          null
        setSeason(currentSeason)
        updateDateRange([
          currentSeason ? dayjs(currentSeason.startDate).toDate() : null,
          currentSeason ? dayjs(currentSeason.endDate).toDate() : null,
        ])
      })
      .with("Months", () => {
        setSelectedMonth(today.toDate())
        updateDateRange([
          dayjs(today).startOf("month").toDate(),
          dayjs(today).endOf("month").toDate(),
        ])
      })
      .with("Weeks", () => {
        setSelectedWeek(today.startOf("week").toDate())
        updateDateRange([
          dayjs(today).startOf("week").toDate(),
          dayjs(today).endOf("week").toDate(),
        ])
      })
      .with("Years", () => {
        setSelectedYear(today.toDate())
        updateDateRange([
          dayjs(today).startOf("year").toDate(),
          dayjs(today).endOf("year").toDate(),
        ])
      })
  }, [plan])

  const { data: authStatus } = useSuspenseQueryGet(
    "/api/Authentication/GetAuthStatus",
    null
  )

  return (
    <>
      <Page>
        <Group w={1000} maw={"100%"} align="start" gap="md">
          <Stack miw={300} maw={"100%"} flex={1}>
            <SelectEntity
              url="/api/Plans/GetAvailablePlans"
              getId={(v) => v.id}
              toRequest={() => null}
              getLabel={(v) => v.name}
              required
              searchable
              label={t("Schedules")}
              placeholder={t("Schedules_SelectSchedulePlaceholder")}
              onChange={(id, entity) => {
                if (id) {
                  setPlan(entity)
                } else {
                  setPlan(null)
                }
                setDateRange([null, null])
                clearInputs()
              }}
              value={plan !== null ? plan.id : null}
            />

            {plan === null
              ? null
              : match(plan.schedulePeriodType)
                  .with(
                    "Days",
                    () =>
                      // normal date picker is always rendered, so for "days" we don't need to add a helper control
                      null
                  )
                  .with(
                    "None",
                    () =>
                      // do not even show date picker for this one? TODO
                      null
                  )
                  .with("Seasons", () => (
                    <Select
                      label={t("Season")}
                      placeholder={t("Schedules_SelectSeasonPlaceholder")}
                      onChange={(v) => {
                        if (v) {
                          const selected = plan.seasons.find(
                            (s) => s.seasonId === parseInt(v)
                          )
                          if (selected) {
                            setSeason(selected)
                            updateDateRange([
                              dayjs(selected.startDate).toDate(),
                              dayjs(selected.endDate).toDate(),
                            ])
                          }
                        }
                      }}
                      data={plan.seasons.map((s) => ({
                        label: s.name,
                        value: s.seasonId.toString(),
                      }))}
                      value={
                        season !== null ? season.seasonId.toString() : null
                      }
                    />
                  ))
                  .with("Months", () => (
                    <MonthPickerInput
                      label={t("Month")}
                      minDate={dayjs(plan?.start).toDate()}
                      maxDate={dayjs(plan?.end).toDate()}
                      value={selectedMonth}
                      onChange={(v) => {
                        updateDateRange([
                          dayjs(v).startOf("month").toDate(),
                          dayjs(v).endOf("month").toDate(),
                        ])
                        setSelectedMonth(v ? new Date(v) : null)
                      }}
                    />
                  ))
                  .with("Weeks", () => (
                    <>
                      <YearPickerInput
                        label={t("Year")}
                        value={selectedYear || new Date()}
                        onChange={(v) =>
                          setSelectedYear(
                            v ? dayjs().set("year", parseInt(v)).toDate() : null
                          )
                        }
                      />
                      <WeekPicker
                        year={selectedYear || new Date()}
                        minDate={dayjs(plan?.start).toDate()}
                        maxDate={dayjs(plan?.end).toDate()}
                        label={t("Week")}
                        placeholder={t("Schedules_SelectWeekPlaceholder")}
                        value={selectedWeek || dayjs().startOf("week").toDate()}
                        onChange={(v) => {
                          updateDateRange([
                            v as Date,
                            dayjs(v as Date)
                              .add(6, "days")
                              .toDate(),
                          ])
                          setSelectedWeek(v)
                        }}
                      />
                    </>
                  ))
                  .with("Years", () => (
                    <YearPickerInput
                      label={t("Year")}
                      minDate={dayjs(plan?.start).toDate()}
                      maxDate={dayjs(plan?.end).toDate()}
                      placeholder={t("Schedules_SelectYearPlaceholder")}
                      value={selectedYear}
                      onChange={(v) => {
                        updateDateRange([
                          dayjs(v).startOf("year").toDate(),
                          dayjs(v).endOf("year").toDate(),
                        ])
                        setSelectedYear(v ? new Date(v) : null)
                      }}
                    />
                  ))
                  .exhaustive()}
            {plan && (
              <>
                <DatePickerInput
                  allowSingleDateInRange
                  type="range"
                  minDate={dayjs(plan?.start).toDate()}
                  maxDate={dayjs(plan?.end).toDate()}
                  label={t("Schedules_DatePickerLabel")}
                  required
                  placeholder={t("Schedules_DatePickerPlaceholder")}
                  value={dateRange}
                  valueFormat="L"
                  onChange={(v) => {
                    updateDateRange([
                      v[0] ? new Date(v[0]) : null,
                      v[1] ? new Date(v[1]) : null,
                    ])
                    clearInputs()
                  }}
                />
                {plan === null ? null : plan.isProductionRequired &&
                  dateRange[0] &&
                  dateRange[1] ? (
                  <SelectEntity
                    label={t("Production")}
                    required
                    placeholder={t("Schedules_SelectProductionPlaceholder")}
                    url="/api/Production/GetProductionsInPeriod"
                    toRequest={() => ({
                      start: dayJstoDateOnly(dayjs(dateRange[0])),
                      end: dayJstoDateOnly(dayjs(dateRange[1])),
                    })}
                    getId={(v) => v.productionId}
                    getLabel={(v) => v.title}
                    value={productionId}
                    searchable
                    onChange={(v) => setProductionId(v || null)}
                  />
                ) : null}
                <FormButtonGroup>
                  <SegmentedControl
                    disabled={!valid}
                    value={format}
                    onChange={(v) => {
                      if (v === "PDF" || v === "Word") {
                        setFormat(v)
                      }
                    }}
                    data={["PDF", "Word"]}
                    style={{ alignSelf: "start" }}
                  />
                  <DefaultButton
                    disabled={!valid}
                    onClick={() => {
                      // maybe us form validation here instaed
                      if (!plan) {
                        showErrorNotification(t("NoScheduleSelected"))
                        return
                      }
                      window.open(createLink({ download: true }), "_blank")
                    }}
                  >
                    {t("Download")}
                  </DefaultButton>
                  {/* <InputLabel mb={-8}>{t("Format")}</InputLabel> */}
                  <PrimaryButton
                    disabled={!valid || format === "Word"}
                    onClick={() => {
                      // maybe us form validation here instaed
                      if (!plan) {
                        showErrorNotification(t("NoScheduleSelected"))
                        return
                      }
                      window.open(createLink({ download: false }), "_blank")
                    }}
                  >
                    {t("Show")}
                  </PrimaryButton>
                </FormButtonGroup>
                <FormButtonGroup justify="end"></FormButtonGroup>
              </>
            )}
          </Stack>
          {authStatus.loggedIn && (
            <Stack miw={300} maw={"100%"} gap={"xs"} flex={1}>
              <Text fz="lg">{t("PublishedPlans")}</Text>
              {!!plan && dateRange[0] && dateRange[1] && (
                <Suspense>
                  <PublishPlanLayoutControls
                    planLayoutId={plan.id}
                    startDate={getDateOnly(dateRange[0])}
                    endDate={getDateOnly(dateRange[1])}
                  />
                </Suspense>
              )}
              {!!plan && (
                <SuspenseWithErrorBoundary>
                  <PublishedPlanList planId={plan.id} />
                </SuspenseWithErrorBoundary>
              )}
            </Stack>
          )}
        </Group>
      </Page>
    </>
  )
}
